import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import cardChip from "../assets/images/card-chip.svg";
import iconTrash from "../assets/images/trash-gif.gif";
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setCardContentType } from '../redux/stripeReducer';
import { cancelSubscription, getProfile, getSubscriptionDetails, createPlaidLinkToken, exchangePlaidPublicToken } from '../shared/_services/common.services';
import { usePlaidLink } from "react-plaid-link";

import PlaidFallbackModal from "./PlaidFallbackModal";

// The public `selected_plan` has two live shapes, both written by current server
// code and both present in the live database: `purchase-subscription` stores the
// raw Stripe Subscription, `change-plan` stores the Stripe Price run through the
// server's formatPriceData (see getSelectedPlanPriceId in shared/_helpers/selectedPlan.js
// for the shape split). The shapes disagree about units: the subscription's
// nested amounts are Stripe's cents, while formatPriceData already divided by
// 100, so the price shape holds dollars — a live price-shape document reads
// `unit_amount: 49.99`. Dividing both would show that user $0.50. Exported for
// tests.
export function getPlanDisplay(selectedPlan) {
  if (!selectedPlan) return { amount: null, interval: null, label: null, lookupKey: null };

  const nestedPrice = selectedPlan.items?.data?.[0]?.price;

  let amount = null;
  if (nestedPrice || selectedPlan.object === "subscription") {
    const cents = nestedPrice?.unit_amount ?? selectedPlan.plan?.amount ?? null;
    amount = cents != null ? cents / 100 : null;
  } else if (selectedPlan.unit_amount != null) {
    amount = selectedPlan.unit_amount;
  }

  const price = nestedPrice ?? selectedPlan;
  const interval = price?.recurring?.interval ?? selectedPlan.plan?.interval ?? null;
  const lookupKey = price?.lookup_key ?? null;
  // Both nickname fields are null on real documents; the lookup key ("Essential
  // price", "Platinum price") is the only usable label, minus its suffix.
  const nickname = price?.nickname ?? selectedPlan.plan?.nickname ?? null;
  const label = nickname ?? (lookupKey ? lookupKey.replace(/\s+price$/i, "") : null);

  return { amount, interval, label, lookupKey };
}

/** "$38.50" / "$99" from a dollar amount; null when there is none. */
export function formatPlanAmount(amount) {
  if (amount == null) return null;
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

// `card` is GET /subscription/details' display object: {brand, last4, expMonth,
// expYear} — camelCase, not Stripe's exp_month/exp_year. Exported for tests.
export function formatCardExpiry(card) {
  if (card?.expMonth == null || card?.expYear == null) return null;
  const month = String(card.expMonth).padStart(2, "0");
  const year = String(card.expYear).slice(-2);
  return `${month}/${year}`;
}

function Subscription({ handleCancelStatus }) {
    const { enqueueSnackbar } = useSnackbar();
    const stripeDetails = JSON.parse(localStorage.getItem('currentUser'))?.user?.selected_plan;
    const userId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;
    const [userData, setUserData] = useState("");
    // GET /subscription/details — cancellation state and the saved-card display
    // fields, which have no other public source since backend SEC-058 removed
    // stripe_details from user responses (#61, backend #289). The actions that
    // depend on it stay disabled until it has loaded: treating "not loaded yet"
    // or "failed" as "not cancelled" would offer a cancelled subscriber the
    // cancel dialog and plan-change navigation.
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [detailsState, setDetailsState] = useState('loading'); // 'loading' | 'loaded' | 'error'
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [iscancelled, setIsCancelled] = useState(false);
    const [linkToken, setLinkToken] = useState(null);
    const token = JSON.parse(localStorage.getItem("currentUser"))?.token;
    
    const [showPlaidModal, setShowPlaidModal] = useState(false);
    const [modalVariant, setModalVariant] = useState('success');
    const [modalMessage, setModalMessage] = useState('');
    const [modalSecondaryMessage, setModalSecondaryMessage] = useState('');

    useEffect(() => {
        if (!token) return;

        createPlaidLinkToken(userId, `${process.env.REACT_APP_URL}/profile`)
            .then((link_token) => setLinkToken(link_token))
            // .catch(() => handleLogout());
            .catch((error) => {
                console.error("Error creating Plaid link token:", error);
                setModalVariant('error');
                setModalMessage('We couldn’t connect your accounts.');
                setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
                setShowPlaidModal(true);
            });
    }, [token, userId]);

    

    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: async (public_token) => {
            const startTime = Date.now();
            try {
                await exchangePlaidPublicToken(public_token, userId);
                setModalVariant('info');
                setModalMessage('Connecting your accounts…');
                setModalSecondaryMessage('This usually takes just a few seconds.\nYour information is encrypted and secure.');
                setShowPlaidModal(true);

                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                    await wait(2000 - elapsed);
                }

                setModalVariant('success');
                setModalMessage('Your accounts are connected!');
                setModalSecondaryMessage('We pulled in the details we could. You can add or edit anything manually — estimates are totally fine.');

            } catch (error) {
                console.error("Error exchanging Plaid token:", error);
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                    await wait(2000 - elapsed);
                }

                setModalVariant('error');
                setModalMessage('We couldn’t connect your accounts.');
                setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
                setShowPlaidModal(true);
            }
        },
    });

    //   popup functions
    const [cancelState, setCancelState] = useState(false);
    const closeModal = () => setCancelState(false);

    useEffect(() => {
        fetchUser();
        fetchSubscriptionDetails();
    }, []); // eslint-disable-line

    const fetchUser = async () => {
        const res = await getProfile(JSON.parse(localStorage.getItem('currentUser'))?.user?._id)
        if (res.status == 200) {

            setUserData(res?.data?.data?.user)
        }
    }

    const fetchSubscriptionDetails = async () => {
        // Every (re)fetch parks the actions until fresh state lands. Without
        // this, the post-cancel refetch window still gates on the stale
        // "not cancelled" payload and briefly re-enables both buttons.
        setDetailsState('loading');
        // 400 means no subscription on this account; the service returns the
        // axios error in that case, so only a real payload is stored.
        const res = await getSubscriptionDetails();
        if (res?.status === 200) {
            setSubscriptionDetails(res?.data);
            setDetailsState('loaded');
        } else {
            setDetailsState('error');
        }
    }

    const plan = getPlanDisplay(userData?.selected_plan);
    const detailsReady = detailsState === 'loaded';
    const cancellationRequested = subscriptionDetails?.cancellationRequested === true;
    const card = subscriptionDetails?.card;

    const openModal = () => {
        if (stripeDetails?.active != 'true') setCancelState(true);
    };

    const handleChangePlan = () => {
        if (!cancellationRequested) {
            navigate('/package', { state: { from: 'changePlan' } });
        }
        else {
            enqueueSnackbar("You have canceled your subscription. You can not change your plan!", {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
            });
        }
    };

    

    const handleAddNewCard = async () => {
        dispatch(setCardContentType("AddNewCard"));
        navigate('/make-payment')
    }

    const handleCancelSubscription = async () => {
        setIsCancelled(true);
        // No subscriptionId in the body: the server cancels the caller's own
        // subscription from their record and 403s a mismatching id (SEC-107).
        let data = await cancelSubscription({});
        if (data.status === 200) {
            handleCancelStatus(true);
            closeModal();
            fetchSubscriptionDetails();
            const res = await getProfile(JSON.parse(localStorage.getItem('currentUser'))?.user?._id)
            if (res.status == 200) {
                setUserData(res?.data?.data?.user)
                // Update localStorage with the new user data
                const currentUser = JSON.parse(localStorage.getItem('currentUser'));
                currentUser.user = res?.data?.data?.user; // Update the user object with the latest data
                localStorage.setItem('currentUser', JSON.stringify(currentUser));
            }
            enqueueSnackbar("Subscription cancelled successfully.", {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
            });
            setIsCancelled(false);

        }

    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <div className="ast-card plan-dtls mb-4">
                        <h4 className="ast-card-heading">Subscription</h4>
                        <div className="d-flex justify-content-between">
                            <div>
                                <div className='d-flex place-items-center gap-10 mb-0'>
                                    {!userData ? (
                                        <div className='flow-loader'></div>
                                    ) : (
                                        <h1 className="plan-price text-right f-700">
                                            {formatPlanAmount(plan.amount) ?? "—"}
                                        </h1>
                                    )}
                                    {plan.interval && (
                                        <h5 className="f-400">/ {plan.interval === 'month' ? 'Month' : 'Year'}</h5>
                                    )}
                                </div>
                                <p className="text-secondary f-400 text-14">
                                    {plan.label ? `${plan.label} Plan` : ""}
                                </p>
                            </div>
                            {/* <div className="place-content-center">
                                {userData?.selected_plan?.unit_amount ? (
                                    <h1 className="plan-price text-right f-700">
                                        ${userData?.selected_plan?.unit_amount}
                                    </h1>
                                ) : (
                                    <div className='flow-loader'></div>
                                )}
                                <h5 className="f-400">Per {userData?.selected_plan?.recurring.interval === 'month' ? 'Month' : 'Year'}</h5>
                            </div> */}
                        </div>
                        <div className="d-flex justify-content-between mt-4">
                            
                            <button
                                onClick={cancellationRequested ? undefined : openModal}
                                disabled={!detailsReady}
                                className={`as-btn-secondary px-4 py-2 ${cancellationRequested
                                    ? "cursor-not-allowed"
                                    : "cursor-pointer"
                                    }`}
                            >
                                {cancellationRequested
                                    ? "Subscription Cancelled"
                                    : "Cancel Subscription"}
                            </button>

                            <button
                                type="button"
                                to="/package"
                                className={`as-btn-primary px-4 py-2 ${plan.lookupKey === "Platinum price" ? 'platinum-btn' : ''}`}
                                onClick={handleChangePlan}
                                disabled={!detailsReady || plan.lookupKey === "Platinum price"}
                            >
                                Change Plan
                            </button>
                        </div>

                    </div>
                </div>
                <div className="col-12 ">
                    <div className="ast-card plan-dtls">
                        <h4 className="ast-card-heading">Payment Method</h4>
                        <div className="payment-card-dtls">
                            <div className="card-company justify-content-end">
                                <h2>
                                    {card?.brand}
                                </h2>
                            </div>
                            <div className="card-number">
                                <h5>****</h5>
                                <h5>****</h5>
                                <h5>****</h5>
                                <h5>{card?.last4}</h5>
                            </div>
                            <div className="card-chip">
                                <img width={30} src={cardChip} alt="" />
                            </div>
                            <div className="d-flex justify-content-between mt-3">
                                <div>
                                    <p className="expiry-date-label">Expiry Date</p>
                                    <p className="expiry-date">{formatCardExpiry(card)}</p>
                                </div>
                            </div>
                            {/* <button onClick={handleAddNewCard}>Activate</button> */}
                        </div>
                        <div className="d-flex justify-content-between mt-4">
                            <button className="as-btn-secondary px-4 py-2" onClick={open} // Use openPlaid from the hook
                                disabled={!ready} >
                                Configure Plaid
                            </button>
                            <button className="as-btn-primary px-4 py-2 d-flex place-items-center" onClick={handleAddNewCard}>
                                <i className="fa fa-plus mr-2" aria-hidden="true"></i>
                                Add New Card
                            </button>
                        </div>
                    </div>
                </div>

                {/* cancel subscription popup */}
                <Modal
                    size="md"
                    className="confirmLogoutPopup customMiniPopup"
                    show={cancelState}
                    onHide={closeModal}
                    aria-labelledby="confirmLogoutPopupLabel"
                    backdrop="static"
                    keyboard={false}
                    centered
                >
                    <div className="modal-body">
                        <div className="d-flex justify-content-between">
                            <h4 className="mt-3"></h4>
                            <Link className="close-popup-btn" onClick={closeModal}>
                                <i className="iconoir-xmark"></i>
                            </Link>
                        </div>
                        <div className="d-flex justify-content-center">
                            <img width={43} src={iconTrash} alt="trash" />
                        </div>
                        <h5 className="mt-3 mb-2 text-center f-400 text-secondary">You are attempting to cancel Subscription</h5>
                        <h5 className="text-center f-700">Are you sure?</h5>
                        <div className="text-center mt-4 mb-3">
                            <button className="as-btn-primary px-4" onClick={handleCancelSubscription}>
                                <i className="fa fa-check mr-2" aria-hidden="true"></i>
                                {iscancelled ? "Cancelling.." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>

            {showPlaidModal && (
                <PlaidFallbackModal variant={modalVariant} message={modalMessage} secondaryMessage={modalSecondaryMessage} onClose={() => setShowPlaidModal(false)} onTryAgain={open} />
            )}
        </>
    )
}

export default Subscription
